import api from './api';

export type OperationSource = 'personal' | 'shared';

export type RecurrenceMode = 'all' | 'one_time' | 'recurring';
export type ValidationMode = 'all' | 'validated' | 'pending';
export type RecurrenceFrequency =
  | 'MONTHLY_DAY'
  | 'MONTHLY_WEEKDAY'
  | 'WEEKLY'
  | 'EVERY_N_WEEKS'
  | 'INSTALLMENTS';

export interface SearchFilters {
  dateStart?: string;
  dateEnd?: string;
  scopes?: OperationSource[];
  personalAccountIds?: string[];
  sharedAccountIds?: string[];
  expenseCategoryIds?: string[];
  expenseSubcategoryIds?: string[];
  incomeCategoryIds?: string[];
  incomeSubcategoryIds?: string[];
  operationType?: 'credit' | 'debit';
  amountMin?: number;
  amountMax?: number;
  recurring?: RecurrenceMode;
  recurrenceFrequencies?: RecurrenceFrequency[];
  validated?: ValidationMode;
  payerPartnerId?: 'partner1' | 'partner2';
  searchText?: string;
  page?: number;
  pageSize?: number;
  sortBy?: 'date' | 'amount';
  sortOrder?: 'asc' | 'desc';
}

export interface SearchResultItem {
  id: string;
  source: OperationSource;
  bankAccount: { id: string; name: string; color: string } | null;
  operationType: 'credit' | 'debit';
  customLabel: string | null;
  amount: number;
  operationDate: string;
  month: number;
  year: number;
  category: {
    id: string;
    name: string;
    icon: string | null;
    type: 'income' | 'expense';
    isDefault: boolean;
  } | null;
  subcategory: {
    id: string;
    name: string;
    icon: string | null;
    isDefault: boolean;
  } | null;
  isRecurring: boolean;
  recurrenceFrequency: string | null;
  recurringGroupId: string | null;
  isInternalTransfer: boolean;
  isValidated: boolean;
  paidBy: string | null;
}

export interface SearchTotals {
  credits: number;
  debits: number;
  net: number;
  count: number;
}

export interface LockedSummary {
  count: number;
  creditsSum: number;
  debitsSum: number;
}

export interface SearchResponse {
  operations: SearchResultItem[];
  totals: SearchTotals;
  pagination: { page: number; pageSize: number; total: number };
  locked: LockedSummary | null;
  effectiveStartDate: string | null;
}

function appendCsv(params: URLSearchParams, key: string, values: string[] | undefined): void {
  if (!values || values.length === 0) return;
  params.set(key, values.join(','));
}

export async function searchOperations(filters: SearchFilters): Promise<SearchResponse> {
  const params = new URLSearchParams();

  if (filters.dateStart) params.set('dateStart', filters.dateStart);
  if (filters.dateEnd) params.set('dateEnd', filters.dateEnd);
  appendCsv(params, 'scopes', filters.scopes);
  appendCsv(params, 'personalAccountIds', filters.personalAccountIds);
  appendCsv(params, 'sharedAccountIds', filters.sharedAccountIds);
  appendCsv(params, 'expenseCategoryIds', filters.expenseCategoryIds);
  appendCsv(params, 'expenseSubcategoryIds', filters.expenseSubcategoryIds);
  appendCsv(params, 'incomeCategoryIds', filters.incomeCategoryIds);
  appendCsv(params, 'incomeSubcategoryIds', filters.incomeSubcategoryIds);
  if (filters.operationType) params.set('operationType', filters.operationType);
  if (filters.amountMin !== undefined) params.set('amountMin', String(filters.amountMin));
  if (filters.amountMax !== undefined) params.set('amountMax', String(filters.amountMax));
  if (filters.recurring) params.set('recurring', filters.recurring);
  appendCsv(params, 'recurrenceFrequencies', filters.recurrenceFrequencies);
  if (filters.validated) params.set('validated', filters.validated);
  if (filters.payerPartnerId) params.set('payerPartnerId', filters.payerPartnerId);
  if (filters.searchText) params.set('searchText', filters.searchText);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
  if (filters.sortBy) params.set('sortBy', filters.sortBy);
  if (filters.sortOrder) params.set('sortOrder', filters.sortOrder);

  const response = await api.get<{ success: boolean; data: SearchResponse }>(
    `/operations/search?${params.toString()}`,
  );
  return response.data.data;
}
